/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React, { lazy, Suspense, type ComponentProps } from 'react';

import { bind } from 'bind-event-listener';
import Loadable from 'react-loadable';

import { jsx } from '@atlaskit/css'; // eslint-disable-line @typescript-eslint/no-unused-vars
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import {
	isStoreAssistant,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { findLastIndex } from '../../controllers/chat-store/findLastIndex';

import { areMessageGroupsEqual } from './are-message-groups-equal';
import { getLastMessageGroupMinHeight } from './getLastMessageGroupMinHeight';
import {
	areRenderVersionsEqual,
	type ExplicitMessageGroupRenderVersion,
	type LatestMessageRendererProps,
	type MessageGroupRenderVersion,
	type OlderMessageRendererProps,
	type RenderMessage,
	type RenderOlderMessage,
} from './message-group-render-stabilization';
import { type VirtualizedMessageGroupsProps } from './virtualized-message-groups';

const VirtualizedMessageGroupsLazyComponent = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant-virtualized-message-groups" */ './virtualized-message-groups'
	).then((module) => ({ default: module.VirtualizedMessageGroups })),
);
Object.assign(VirtualizedMessageGroupsLazyComponent, {
	displayName: 'lazy(VirtualizedMessageGroups)',
});

const VirtualizedMessageGroupsLoadableComponent = Loadable<VirtualizedMessageGroupsProps, never>({
	loader: () =>
		import(
			/* webpackChunkName: "@atlassian/conversation-assistant-virtualized-message-groups" */ './virtualized-message-groups'
		).then((module) => module.VirtualizedMessageGroups),
	loading: () => null,
});

function VirtualizedMessageGroupsWithSuspense(
	props: ComponentProps<typeof VirtualizedMessageGroupsLazyComponent>,
): React.JSX.Element {
	return (
		<Suspense fallback={null}>
			<VirtualizedMessageGroupsLazyComponent {...props} />
		</Suspense>
	);
}

const VirtualizedMessageGroups: React.FC<
	ComponentProps<typeof VirtualizedMessageGroupsLazyComponent> & React.RefAttributes<unknown>
> = (props) =>
	fg('nike_r19_react_loadable') ? (
		<VirtualizedMessageGroupsWithSuspense {...props} />
	) : (
		<VirtualizedMessageGroupsLoadableComponent {...props} />
	);

type BaseMessageGroupsContentProps = {
	conversationId: string;
	messageGroups: Array<Array<StoreChatItem>>;
	/** Used only by the virtualized path to render historical groups above the stable anchor. */
	olderMessageGroups: Array<Array<StoreChatItem>>;
	isVirtualizationEnabled: boolean;
	isRenderStabilizationEnabled: boolean;
	chatHistoryContainerRef: React.RefObject<HTMLDivElement | null>;
	isScrollPending: boolean;
	lastMessageGroupRef: React.RefObject<HTMLDivElement>;
	renderMessage: RenderMessage;
	scrollToBottomGeneration: number;
};

export type MessageGroupsContentProps = BaseMessageGroupsContentProps &
	LatestMessageRendererProps &
	OlderMessageRendererProps;

type OlderMessageGroupContentProps = {
	conversationId: string;
	group: Array<StoreChatItem>;
	renderMessage: RenderOlderMessage;
	renderMessageVersion: MessageGroupRenderVersion;
};

const OlderMessageGroupContent = ({
	conversationId: _conversationId,
	group,
	renderMessage,
	renderMessageVersion: _renderMessageVersion,
}: OlderMessageGroupContentProps): JSX.Element => {
	// extra <div> to prevent re-creation, see https://atlassian.slack.com/archives/C0677M19GU9/p1725849002478889
	return (
		<Box testId="rovo-chat-message-group">
			<div>{group.map((msg, index) => renderMessage(msg, index))}</div>
		</Box>
	);
};

const MemoizedOlderMessageGroupContent = React.memo(
	OlderMessageGroupContent,
	(previousProps, nextProps) =>
		previousProps.conversationId === nextProps.conversationId &&
		areMessageGroupsEqual(previousProps.group, nextProps.group) &&
		// Keep a behavior version in the comparator for closure safety. Function identity
		// changes during streaming can be unrelated to already-rendered older groups.
		areRenderVersionsEqual(previousProps.renderMessageVersion, nextProps.renderMessageVersion),
);

type LatestMessageGroupContentProps = {
	conversationId: string;
	group: Array<StoreChatItem>;
	groupIndex: number;
	lastMessageGroupRef: React.RefObject<HTMLDivElement>;
	minHeight: number | undefined;
	renderMessage: (
		message: StoreChatItem,
		index: number,
		isLastAssistant: boolean,
	) => React.ReactNode;
	renderMessageVersion: MessageGroupRenderVersion;
	totalGroups: number;
};

type LatestMessageGroupMessagesProps = Pick<
	LatestMessageGroupContentProps,
	| 'conversationId'
	| 'group'
	| 'groupIndex'
	| 'renderMessage'
	| 'renderMessageVersion'
	| 'totalGroups'
>;

const LatestMessageGroupMessages = ({
	conversationId: _conversationId,
	group,
	groupIndex: _groupIndex,
	renderMessage,
	renderMessageVersion: _renderMessageVersion,
	totalGroups: _totalGroups,
}: LatestMessageGroupMessagesProps): JSX.Element => {
	const lastAssistantMessageInGroupIndex = findLastIndex(group, (msg) => isStoreAssistant(msg));

	return (
		<div>
			{group.map((msg, index) => {
				return renderMessage(msg, index, lastAssistantMessageInGroupIndex === index);
			})}
		</div>
	);
};

const MemoizedLatestMessageGroupMessages = React.memo(
	LatestMessageGroupMessages,
	(previousProps, nextProps) =>
		previousProps.conversationId === nextProps.conversationId &&
		areMessageGroupsEqual(previousProps.group, nextProps.group) &&
		previousProps.groupIndex === nextProps.groupIndex &&
		previousProps.totalGroups === nextProps.totalGroups &&
		areRenderVersionsEqual(previousProps.renderMessageVersion, nextProps.renderMessageVersion),
);

const LatestMessageGroupContent = ({
	conversationId,
	group,
	groupIndex,
	lastMessageGroupRef,
	minHeight,
	renderMessage,
	renderMessageVersion,
	totalGroups,
}: LatestMessageGroupContentProps): JSX.Element => {
	return (
		<Box
			ref={lastMessageGroupRef}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={
				minHeight !== undefined
					? {
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
							minHeight,
						}
					: {}
			}
			testId="rovo-chat-message-group"
		>
			<MemoizedLatestMessageGroupMessages
				conversationId={conversationId}
				group={group}
				groupIndex={groupIndex}
				renderMessage={renderMessage}
				renderMessageVersion={renderMessageVersion}
				totalGroups={totalGroups}
			/>
		</Box>
	);
};

const MemoizedLatestMessageGroupContent = React.memo(
	LatestMessageGroupContent,
	(previousProps, nextProps) =>
		previousProps.conversationId === nextProps.conversationId &&
		areMessageGroupsEqual(previousProps.group, nextProps.group) &&
		previousProps.groupIndex === nextProps.groupIndex &&
		previousProps.totalGroups === nextProps.totalGroups &&
		previousProps.minHeight === nextProps.minHeight &&
		areRenderVersionsEqual(previousProps.renderMessageVersion, nextProps.renderMessageVersion),
);

export const MessageGroupsContent = ({
	conversationId,
	messageGroups,
	olderMessageGroups,
	isVirtualizationEnabled,
	isRenderStabilizationEnabled,
	chatHistoryContainerRef,
	isScrollPending,
	lastMessageGroupRef,
	renderLatestMessage,
	renderLatestMessageVersion,
	renderOlderMessage,
	renderOlderMessageVersion,
	renderMessage,
	scrollToBottomGeneration,
}: MessageGroupsContentProps): JSX.Element => {
	const [chatHistoryContainerHeight, setChatHistoryContainerHeight] = React.useState<
		number | undefined
	>(undefined);
	const resolvedRenderOlderMessage =
		renderOlderMessage ?? ((msg: StoreChatItem, index: number) => renderMessage(msg, index, false));
	const resolvedRenderOlderMessageVersion: ExplicitMessageGroupRenderVersion =
		renderOlderMessageVersion ?? renderMessage;
	const resolvedRenderLatestMessage = renderLatestMessage ?? renderMessage;
	const resolvedRenderLatestMessageVersion: ExplicitMessageGroupRenderVersion =
		renderLatestMessageVersion ?? renderMessage;
	const chatHistoryContainerElement = chatHistoryContainerRef.current;

	React.useEffect(() => {
		const element = chatHistoryContainerRef.current;
		if (!element) {
			setChatHistoryContainerHeight(undefined);
			return;
		}

		const updateHeight = () => {
			const nextHeight = element.clientHeight || undefined;
			setChatHistoryContainerHeight((previousHeight) =>
				previousHeight === nextHeight ? previousHeight : nextHeight,
			);
		};

		updateHeight();

		if (typeof ResizeObserver !== 'undefined') {
			const resizeObserver = new ResizeObserver(updateHeight);
			resizeObserver.observe(element);
			return () => {
				resizeObserver.disconnect();
			};
		}

		return bind(window, {
			type: 'resize',
			listener: updateHeight,
		});
	}, [chatHistoryContainerElement, chatHistoryContainerRef]);

	const latestGroupMinHeight =
		chatHistoryContainerHeight && !isScrollPending
			? getLastMessageGroupMinHeight(chatHistoryContainerHeight)
			: undefined;

	return (
		<UFOSegment name={'message-groups'}>
			{isVirtualizationEnabled ? (
				<VirtualizedMessageGroups
					conversationId={conversationId}
					messageGroups={messageGroups}
					olderMessageGroups={olderMessageGroups}
					chatHistoryContainerRef={chatHistoryContainerRef}
					isRenderStabilizationEnabled={isRenderStabilizationEnabled}
					isScrollPending={isScrollPending}
					lastMessageGroupRef={lastMessageGroupRef}
					renderLatestMessage={resolvedRenderLatestMessage}
					renderLatestMessageVersion={resolvedRenderLatestMessageVersion}
					renderOlderMessage={resolvedRenderOlderMessage}
					renderOlderMessageVersion={resolvedRenderOlderMessageVersion}
					renderMessage={renderMessage}
					scrollToBottomGeneration={scrollToBottomGeneration}
				/>
			) : (
				messageGroups.map((group, i) => {
					const isLastGroup = i === messageGroups.length - 1;

					if (!isLastGroup) {
						return isRenderStabilizationEnabled ? (
							<MemoizedOlderMessageGroupContent
								key={i}
								conversationId={conversationId}
								group={group}
								renderMessage={resolvedRenderOlderMessage}
								renderMessageVersion={resolvedRenderOlderMessageVersion}
							/>
						) : (
							<OlderMessageGroupContent
								key={i}
								conversationId={conversationId}
								group={group}
								renderMessage={(msg, index) => renderMessage(msg, index, false)}
								renderMessageVersion={renderMessage}
							/>
						);
					}

					return isRenderStabilizationEnabled ? (
						<MemoizedLatestMessageGroupContent
							key={i}
							conversationId={conversationId}
							group={group}
							groupIndex={i}
							lastMessageGroupRef={lastMessageGroupRef}
							minHeight={latestGroupMinHeight}
							renderMessage={resolvedRenderLatestMessage}
							renderMessageVersion={resolvedRenderLatestMessageVersion}
							totalGroups={messageGroups.length}
						/>
					) : (
						<LatestMessageGroupContent
							key={i}
							conversationId={conversationId}
							group={group}
							groupIndex={i}
							lastMessageGroupRef={lastMessageGroupRef}
							minHeight={latestGroupMinHeight}
							renderMessage={renderMessage}
							renderMessageVersion={renderMessage}
							totalGroups={messageGroups.length}
						/>
					);
				})
			)}
		</UFOSegment>
	);
};
